/* eslint-disable react/no-danger */
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';

import {
  ROOM_SHARED,
  LISTING_TYPES,
  getTranslatedPropertyType
} from '../../../../data/common/property';

import Price from '../../common/Price';
import Tag from '../../../Tag';
import BaseChip from '../../../BaseChip';
import Scoring from '../../../Scoring';

import HomecardContentSimplified from './HomecardContentRedesign';
import HomecardContentSeparator from './HomecardContentSeparator';
import HomecardContentDescription from './HomecardContentDescription';

import styles from './HomecardContent.module.scss';

const DEFAULT_LISTING_TYPE = ROOM_SHARED;

const HomecardContentBooked = ({ className, withTransparentBackground }) => {
  return (
    <div
      className={classNames(
        styles['homecard-content'],
        {
          [styles['homecard-content--with-transparent-background']]:
            withTransparentBackground
        },
        className
      )}
      data-test="homecard-content"
    >
      <span className={styles['homecard-content__header']}>
        <span className={styles['homecard-content__booked']}>
          {trans('homecard.is-booked.description')}
        </span>
      </span>
    </div>
  );
};

const HomecardContent = ({
  availableFrom,
  title,
  link,
  target,
  onClick,
  price = {},
  type = DEFAULT_LISTING_TYPE,
  chips = [],
  withTransparentBackground = false, // To review if necessary (all of our homecards are white)
  onMoreInfoClick = () => {},
  hasDescription = false,
  descriptionPosition = 'default',
  offerDiscount = null,
  isBooked = false,
  isEmbedded = false,
  scoring = {},
  className = ''
}) => {
  const SimplifyHomecardsDesign = useSoyuzExperiment('SimplifyHomecardsDesign');

  if (isBooked) {
    return (
      <HomecardContentBooked
        className={className}
        withTransparentBackground={withTransparentBackground}
      />
    );
  }

  const isSimplifyHomecardsDesignVariant =
    SimplifyHomecardsDesign.get('behaviour') === 'variant';

  if (isSimplifyHomecardsDesignVariant) {
    return (
      <HomecardContentSimplified
        availableFrom={availableFrom}
        title={title}
        link={link}
        target={target}
        onClick={onClick}
        price={price}
        type={type}
        chips={chips}
        withTransparentBackground
        onMoreInfoClick={onMoreInfoClick}
        hasDescription={hasDescription}
        descriptionPosition={descriptionPosition}
        offerDiscount={offerDiscount}
        isBooked={isBooked}
        isEmbedded={isEmbedded}
        scoring={scoring}
        className={className}
      />
    );
  }

  const hasChips = chips.length;
  const showDefaultDescription =
    hasDescription && descriptionPosition === 'default';

  const showDescriptionAtBottom =
    hasDescription && descriptionPosition === 'bottom';

  return (
    <div
      className={classNames(
        styles['homecard-content'],
        {
          [styles['homecard-content--with-transparent-background']]:
            withTransparentBackground
        },
        className
      )}
      data-test="homecard-content"
    >
      <a
        className={styles['homecard-content__header']}
        href={link}
        target={target}
        onClick={onClick}
        data-test="homecard-link"
      >
        <div className={styles['homecard-content__info-container']}>
          <span
            className={classNames(styles['homecard-content__type'], {
              [styles['homecard-content__type--embedded']]: isEmbedded
            })}
          >
            {getTranslatedPropertyType(type)}
          </span>
          <HomecardContentSeparator />
          <span
            className={classNames(styles['homecard-content__available-from'], {
              [styles['homecard-content__available-from--embedded']]: isEmbedded
            })}
          >
            {trans('homecard.available-from.rebranding', {
              ordinalDate: availableFrom
            })}
          </span>
          {scoring?.score ? (
            <div className={styles['homecard-content__scoring-container']}>
              <HomecardContentSeparator />
              <Scoring
                score={scoring.score}
                totalReviews={scoring.totalBookingsClosed}
              />
            </div>
          ) : null}
        </div>
        <p
          className={classNames(styles['homecard-content__title'], {
            [styles['homecard-content__title--embedded']]: isEmbedded
          })}
        >
          {title}
        </p>
      </a>

      <div className={styles['homecard-content__footer']}>
        <div className={styles['homecard-content__price-container']}>
          <Price
            {...price}
            color="dark"
            className={styles['homecard-content__price']}
            biggerFont={isEmbedded}
            biggerFontMonthly={isEmbedded}
          />
          {offerDiscount && (
            <Tag
              className={styles['homecard-content__price-tag']}
              colorSchema="pink"
              size="small"
              label={trans('homecard.label.up_to_discount', {
                discount: offerDiscount
              }).toUpperCase()}
            />
          )}
        </div>
        {showDefaultDescription && (
          <HomecardContentDescription onMoreInfoClick={onMoreInfoClick} />
        )}
        {hasChips ? (
          <div className={styles['homecard-content__label-container']}>
            {chips.map(chip => (
              <Tag
                size="small"
                colorSchema={chip.colorSchema || 'tag'}
                key={chip.label}
                {...chip}
              />
            ))}
          </div>
        ) : null}
        {showDescriptionAtBottom && (
          <HomecardContentDescription
            position="bottom"
            onMoreInfoClick={onMoreInfoClick}
          />
        )}
      </div>
    </div>
  );
};

HomecardContent.propTypes = {
  availableFrom: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]).isRequired,
  price: PropTypes.shape(Price.propTypes),
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(LISTING_TYPES),
  chips: PropTypes.arrayOf(PropTypes.shape(BaseChip.propTypes)),
  withTransparentBackground: PropTypes.bool,
  onMoreInfoClick: PropTypes.func,
  hasDescription: PropTypes.bool,
  descriptionPosition: PropTypes.oneOf(['bottom', 'default']),
  offerDiscount: PropTypes.string,
  link: PropTypes.string.isRequired,
  target: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isBooked: PropTypes.bool,
  area: PropTypes.number,
  roomArea: PropTypes.number,
  showSquaredMeters: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  scoring: PropTypes.shape({
    score: PropTypes.number,
    totalReviews: PropTypes.number
  }),
  className: PropTypes.string
};

export default HomecardContent;
